/**
 * RWBR — THE DISPATCH, WRITTEN WITHOUT A MODEL. D-EZ-51.
 *
 * Howard, 18 Aug 2026: *"if they dont add one then the commentary is simply
 * fallback cached commentary with no api calls."*
 *
 * ===========================================================================
 * THIS IS NOT A PLACEHOLDER AND IT MUST NOT READ LIKE ONE
 * ===========================================================================
 *
 * Most players will never paste an API key. If the no-key path prints "AI
 * narration unavailable" in a grey box, then for most people this feature does
 * not exist — and the feature Howard asked for is the newspaper, not the model.
 * So this file writes a real article: a headline chosen from what actually
 * happened, three or four paragraphs of ordinary newspaper prose, a byline and a
 * dateline, in the same shape and the same modal as the written-by-Luna one. The
 * only difference a player sees is one honest line at the foot of it.
 *
 * ===========================================================================
 * HOW IT AVOIDS SOUNDING THE SAME EVERY WEEK
 * ===========================================================================
 *
 * Two devices, and neither is randomness — a paper that reads differently every
 * time you open the same edition is a paper nobody trusts:
 *
 *  1. THE LEAD IS CHOSEN, NOT TEMPLATED. `leadAngle` ranks what happened —
 *     a first career win beats a win from deep, which beats a change at the top
 *     of the championship, which beats a routine win — and the headline and
 *     first paragraph follow whichever won. Weeks differ because races differ.
 *
 *  2. PHRASING ROTATES ON THE RACE NUMBER. Where two sentences would do the same
 *     job, the edition number picks one. Deterministic, so race 14 reads the
 *     same way every time you open it, and race 15 does not read like race 14.
 *
 * ⚠ AND IT OBEYS EVERY RULE THE MODEL OBEYS. No quotes, no invented result, no
 * adjective doing a fact's work. `paperFacts.ts` is the only input; if it is not
 * in the facts it is not in the paper. The one thing this writer has that the
 * model does not is certainty — it cannot get a number wrong, because it never
 * writes one it was not handed.
 */

import { toDollars } from '../finance/money.ts';
import type { MilestoneKind, PaperFacts } from './paperFacts.ts';
import { angleFor, type PaperAngle } from './angle.ts';
import { bankQuote } from './quoteBank.ts';

/**
 * ⚠ DUPLICATED FROM `worker/paper.ts`, DELIBERATELY.
 *
 * The Worker holds the authoritative copy because a masthead and a byline must
 * not be nameable from a request body — the browser composes those requests. But
 * this writer runs with no Worker involved at all, so it needs its own. They are
 * not secrets; they are identity. If one changes, change both — `paper.test.ts`
 * asserts they agree.
 */
export const MASTHEAD = 'THE STOCK CAR DISPATCH';
export const MOTTO = 'America’s racing weekly';
export const DESK: readonly string[] = [
  'Cal Renfro',
  'Dot Whitmire',
  'Bud Speight',
  'Loretta Kinard',
  'Roy Ellis Todd',
  'Marva Sizemore',
];

export function bylineFor(index: number): string {
  const n = Number.isFinite(index) ? Math.abs(Math.floor(index)) : 0;
  return DESK[n % DESK.length];
}

export interface PaperArticle {
  masthead: string;
  motto: string;
  edition: 'race' | 'season' | 'winter';
  headline: string;
  standfirst: string;
  byline: string;
  dateline: string;
  paragraphs: string[];
  source: 'ai' | 'fallback';
  model: string | null;
  paidBy: 'byo' | 'server' | 'nobody';
  /**
   * D-EZ-70 — the assignment this edition was written to. Optional because
   * every article cached before it existed has none, and an edition on file is
   * not re-bought to acquire one.
   */
  angle?: PaperAngle;
}

// ---------------------------------------------------------------------------
// Small words
// ---------------------------------------------------------------------------

const ORDINALS = [
  '', 'first', 'second', 'third', 'fourth', 'fifth', 'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
];

function ordinal(n: number): string {
  const rem100 = n % 100;
  const suffix =
    rem100 >= 11 && rem100 <= 13
      ? 'th'
      : n % 10 === 1
        ? 'st'
        : n % 10 === 2
          ? 'nd'
          : n % 10 === 3
            ? 'rd'
            : 'th';
  return `${n}${suffix}`;
}

function wordOrdinal(n: number): string {
  return ORDINALS[n] ?? ordinal(n);
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** A list, joined the way a person writes one. */
function list(items: readonly string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * Megabucks to the figure a newspaper prints.
 *
 * ⚠ `toDollars`, NEVER A MULTIPLICATION WRITTEN HERE. A Megabuck is $10,000
 * (SAG/14) and this file had it as $1,000,000 for about an hour on 18 Aug 2026 —
 * long enough for a rendered proof to print a driver's season winnings as $358.8
 * million. It was caught by LOOKING at the page, not by a test, and the reason
 * no test caught it is that nothing in the fallback writer knew the right
 * answer: the conversion lives in `money.ts` and every reader has to ask it
 * rather than remember it. `paper.test.ts` now pins the unit.
 *
 * Spelled out rather than abbreviated — `formatDollars` writes "$3.59M" for a
 * broadcast graphic, and a newspaper sentence says "$3.59 million".
 */
export function dollars(mb: number): string {
  const usd = toDollars(mb);
  if (usd >= 1_000_000) {
    const millions = usd / 1_000_000;
    return `$${millions >= 10 ? millions.toFixed(1) : millions.toFixed(2)} million`;
  }
  return `$${Math.round(usd).toLocaleString('en-US')}`;
}

/**
 * The one place `outReason` becomes English — and it needs a plural, because
 * these clauses are GROUPED. "Turnage, Wingo and Deaton with a broken car" reads
 * as three men sharing one.
 */
const OUT_WORDS: Record<string, { one: string; many: string }> = {
  trouble: { one: 'in a wreck', many: 'in wrecks' },
  problem: { one: 'with a broken car', many: 'with broken cars' },
  feud: { one: 'after being put into the wall', many: 'after being put into the wall' },
  fuel: { one: 'out of fuel', many: 'out of fuel' },
  'black-flag': { one: 'under the black flag', many: 'under the black flag' },
  'repair-failed': { one: 'behind the wall for good', many: 'behind the wall for good' },
  injury: { one: 'hurt', many: 'hurt' },
};

const outWord = (reason: string, count = 1): string => {
  const words = OUT_WORDS[reason];
  if (!words) return 'out of the race';
  return count > 1 ? words.many : words.one;
};

// ---------------------------------------------------------------------------
// What the piece is about
// ---------------------------------------------------------------------------

export type LeadAngle =
  | 'first-career-win'
  | 'won-from-deep'
  | 'new-points-leader'
  | 'streak'
  | 'champion-crowned'
  | 'routine-win'
  | 'no-race';

/**
 * WHAT HAPPENED THAT IS WORTH THE TOP OF THE PAGE.
 *
 * Ranked, and the ranking is the editorial judgement in this file. A man's first
 * win is a bigger story than a points lead changing hands, and a points lead
 * changing hands is a bigger story than the favourite winning again — that is
 * how a racing weekly would run it, and it is why two consecutive editions do
 * not open the same way unless two consecutive races were the same race.
 *
 * ⚠ D-EZ-70 — THIS NOW READS THE DESK'S ANGLE RATHER THAN DECIDING ITS OWN.
 *
 * The idea started here and the bought article never had it, which is why the
 * no-key edition was the more varied of the two. `angle.ts` is that judgement
 * taken further — it reads the incidents, the attrition and the closing miles,
 * not just the winner — and both editions are now written to the same call.
 *
 * The seven names below stay because this file's prose is written against them,
 * and mapping is honest work: an angle this file has no separate opening for
 * lands on the closest one it does. A `routine-win` lead on a `the-big-one`
 * afternoon is a plainer piece than the model's, which is exactly what the
 * no-key edition is.
 */
export function leadAngle(facts: PaperFacts): LeadAngle {
  return leadFor(angleFor(facts));
}

/** The desk's angle, in the vocabulary this file's prose is written against. */
export function leadFor(angle: PaperAngle): LeadAngle {
  switch (angle) {
    case 'first-win':
      return 'first-career-win';
    case 'from-deep':
    case 'charge':
      return 'won-from-deep';
    case 'title-turn':
      return 'new-points-leader';
    case 'streak':
    case 'runaway':
      return 'streak';
    case 'first-title':
    case 'close-title':
    case 'runaway-title':
    case 'another-title':
    case 'routine-season':
      return 'champion-crowned';
    default:
      // 'the-big-one', 'duel', 'attrition', 'routine' — the afternoon leads and
      // `incidentPara` is already built from the same incidents that named it.
      return 'routine-win';
  }
}

// ---------------------------------------------------------------------------
// The article
// ---------------------------------------------------------------------------

export function fallbackPaper(facts: PaperFacts): PaperArticle {
  const deskAngle = angleFor(facts);
  const angle = leadFor(deskAngle);
  const turn = facts.racesRun; // the rotation seed: deterministic, per edition
  const paragraphs =
    facts.edition === 'winter'
      ? winterParagraphs(facts)
      : facts.edition === 'season'
      ? [seasonLead(facts), seasonBody(facts), leagueNewsPara(facts), seasonClose(facts)].filter(Boolean)
      : [
          raceLead(facts, angle, turn),
          incidentPara(facts),
          /*
            D-EZ-71b — WHAT HE SAID, WHEN THERE WAS SOMEBODY TO SAY IT.

            Placed after the afternoon and before the championship, which is
            where a newspaper puts it: the reader has just been told what
            happened to the man, and now hears from him. Empty on a race where
            the dice landed in an empty category — about half of them — and the
            piece simply closes up around it.
          */
          quotePara(facts),
          championshipPara(facts, turn),
          attritionPara(facts),
          closePara(facts, turn),
        ].filter(Boolean);

  return {
    masthead: MASTHEAD,
    motto: MOTTO,
    edition: facts.edition,
    headline: headlineFor(facts, angle),
    standfirst: standfirstFor(facts, angle),
    byline: bylineFor(facts.racesRun + facts.season),
    dateline: datelineFor(facts),
    paragraphs: paragraphs as string[],
    source: 'fallback',
    model: null,
    paidBy: 'nobody',
    angle: deskAngle,
  };
}

export function datelineFor(facts: PaperFacts): string {
  const where = facts.race?.location ?? facts.race?.track ?? '';
  return where.toUpperCase();
}

function headlineFor(facts: PaperFacts, angle: LeadAngle): string {
  const race = facts.race;
  const track = shortTrack(race?.track ?? '');
  if (facts.edition === 'winter') return winterHeadline(facts);
  if (facts.edition === 'season') {
    const champ = facts.championship.championName ?? facts.championship.leaderName;
    return `${champ} takes the championship`;
  }
  if (!race) return `The ${facts.league} season continues`;
  const w = race.winner;
  switch (angle) {
    case 'first-career-win':
      return `${surname(w.name)} wins his first at ${track}`;
    case 'won-from-deep':
      return `${surname(w.name)} wins from ${ordinal(w.started ?? 0)} at ${track}`;
    case 'new-points-leader':
      // Surnames, because the full version of this headline ran to two lines in
      // the modal and a headline that wraps twice is a paragraph.
      return `${surname(w.name)} wins as ${surname(facts.championship.leaderName)} takes the points lead`;
    case 'streak':
      return `${surname(w.name)} makes it ${wordOrdinal(w.seasonWins)} on the year`;
    default:
      return `${surname(w.name)} wins the ${race.name}`;
  }
}

function standfirstFor(facts: PaperFacts, angle: LeadAngle): string {
  const c = facts.championship;
  if (facts.edition === 'winter') return winterStandfirst(facts);
  if (facts.edition === 'season') {
    return `${c.leaderName} closes the year on ${plural(c.leaderPoints, 'point')} from ${plural(
      c.leaderWins,
      'win',
    )}.`;
  }
  const race = facts.race;
  if (!race) return '';
  if (angle === 'new-points-leader') {
    return `${c.leaderName} leads by ${plural(c.marginToSecond, 'point')} with ${plural(
      facts.racesRemaining,
      'race',
    )} left.`;
  }
  return `${c.leaderName} still leads the standings by ${plural(c.marginToSecond, 'point')} after ${
    facts.racesRun
  } of ${facts.racesInSeason}.`;
}

function raceLead(facts: PaperFacts, angle: LeadAngle, turn: number): string {
  const race = facts.race;
  if (!race) return '';
  const w = race.winner;
  const from = w.started === null ? '' : ` from ${ordinal(w.started)} on the grid`;
  const led =
    w.turnsLed > 0
      ? ` He led ${plural(w.turnsLed, 'turn')}${w.ledMost ? ', more than anybody' : ''} — ${w.milesOutFront.toLocaleString(
          'en-US',
        )} miles out front.`
      : ' He never led until it mattered.';

  const where = trackInProse(race.track);
  const opener =
    angle === 'first-career-win'
      ? `${w.name} won the ${race.name} at ${where}, the first victory of a career now ${plural(
          w.careerStarts,
          'start',
        )} long.`
      : angle === 'won-from-deep'
        ? `${w.name} started ${ordinal(w.started ?? 0)} and won the ${race.name} at ${where}.`
        : turn % 2 === 0
          ? `${w.name} won the ${race.name} at ${where}${from}.`
          : `The ${race.name} at ${where} went to ${w.name}${from}.`;

  const runners =
    race.top5.length > 1
      ? ` ${list(race.top5.slice(1).map((d) => `${d.name} ${ordinal(d.position)}`))}.`
      : '';

  const trafficBits: string[] = [];
  if (race.cautions !== null && race.cautions > 0) trafficBits.push(plural(race.cautions, 'caution'));
  if (race.leadChanges !== null && race.leadChanges > 0)
    trafficBits.push(plural(race.leadChanges, 'lead change'));
  const traffic = trafficBits.length > 0 ? ` There were ${list(trafficBits)}.` : '';

  return `${opener}${led}${runners}${traffic}`;
}

/**
 * WHAT HAPPENED OUT THERE — D-EZ-54, the no-model version.
 *
 * Howard: *"Is it possible for the end of race article to also include some of
 * the happenings and excitement and troubles etc that happened within the
 * particular race as opposed to solely recapping stats?"* The model's article
 * gets `facts.race.incidents` and a brief telling it to build the body of the
 * piece out of them; this is the same material, told plainly, so the no-key
 * edition covers the afternoon too rather than only the arithmetic.
 *
 * ⚠ THE ENGINE'S SENTENCES ARE USED, NOT REWRITTEN. Every incident's `what` is
 * the rule's own account, and it already names the men. Paraphrasing here would
 * be this file guessing at what a rule meant — the exact thing `paperFacts.ts`
 * refuses to do. What IS stripped is the ledger's furniture: the rule citation
 * on the front and the dice arithmetic in the middle, which belong in the ledger
 * a reader can open and not in a newspaper column.
 */
function incidentPara(facts: PaperFacts): string {
  const incidents = facts.race?.incidents ?? [];
  if (incidents.length === 0) return '';

  // The four biggest moments, told in the order they happened.
  const chosen = [...incidents]
    .sort((a, b) => b.weight - a.weight)
    .slice(0, 4)
    .sort((a, b) => a.turn - b.turn);

  // Varied openers, chosen by position rather than at random: four sentences
  // all starting "On turn N," is a list wearing a paragraph's clothes.
  const OPENERS = ['On turn', 'Then on turn', 'At turn', 'And on turn'];
  const sentences = chosen.map((incident, i) => {
    const cleaned = forThePaper(incident.what);
    return `${OPENERS[i % OPENERS.length]} ${incident.turn}, ${lowerFirst(cleaned)}`;
  });
  return sentences.join(' ');
}

/**
 * The quote, set as a newspaper sets one: the words, then the man, then a short
 * clause of context the ledger supports.
 *
 * ⚠ THE QUOTATION MARKS ARE ADDED HERE AND NOWHERE ELSE. `forThePaper` strips
 * every mark out of the engine's own sentences precisely so that a mark in this
 * article means a person said something — and there is exactly one place in this
 * file that can put one in.
 */
function quotePara(facts: PaperFacts): string {
  const quote = bankQuote(facts);
  if (!quote) return '';
  const iv = facts.race?.interview;
  const context =
    iv?.band === 'wrecked'
      ? ', who was collected out of the race'
      : iv?.band === 'dnf'
        ? ', whose car did not last the distance'
        : iv?.band === 'feud' || iv?.band === 'fume'
          ? ', who had words for somebody afterwards'
          : '';
  return `\u201C${quote.text}\u201D ${quote.speaker}${context} ${quote.attribution}.`;
}

/** The ledger's furniture, removed. The sentence underneath is untouched. */
export function forThePaper(text: string): string {
  return (
    text
      // "TROUBLE RESULT (p.v): an wreck in the top group" → "a wreck in the…"
      .replace(/^[A-Z][A-Z\s]{2,}(?:\([^)]*\))?:\s*/, '')
      // "— red 1 inside 2 gas —" and "— red 5 against 2 gas." The dice are the
      // ledger's business; a newspaper reports the pass, not the arithmetic.
      .replace(/\s*—\s*(?:red|blue|white)\b[^—.]*(?:—|(?=\.))/gi, '')
      .replace(/\s*\(p\.[ivxlc]+\)/gi, '')
      // "Metallic 9: " — which chart cell fired. Ledger furniture again.
      .replace(/\b(?:Metallic|Black|Red|Blue|White)\s+\d+:\s*/g, '')
      /*
        ⚠ AND THE QUOTATION MARKS COME OFF.

        Some engine descriptions quote a printed chart cell verbatim —
        `Metallic 9: "Turtle OUT + YELLOW"`. That is a quotation from a rulebook
        and not a fabricated quote from a person, so it is honest; it is also
        indistinguishable, to a reader of a newspaper, from a driver being
        quoted. The paper's no-quotes rule is absolute precisely BECAUSE it is
        absolute — it is the one invention a machine can check for — so the
        marks come off and the words stay. Caught by `paper.test.ts`, which
        refused the article rather than letting it through.
      */
      .replace(/["“”„‟«»‹›]/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim()
  );
}

/**
 * Lower the first letter so an engine sentence can follow "On turn 12,".
 *
 * ⚠ NOT WHEN IT IS SOMEBODY'S NAME. Found in a screenshot: "At turn 50, dale
 * Ballew gets alongside Rip Trantham" and "And on turn 50, del Grubbs gets
 * alongside Shad Ficklin". The old rule lowered any capital whose next letter
 * was lower case, which is every first name there has ever been.
 *
 * Two capitalised words in a row is a person. One capital followed by an
 * ordinary word — "A wreck in the bottom group" — is a sentence, and that is
 * the case this exists for.
 */
const lowerFirst = (s: string): string => {
  const words = s.split(/\s+/);
  if (words.length > 1 && /^[A-Z][a-z]/.test(words[0] ?? '') && /^[A-Z]/.test(words[1] ?? '')) return s;
  return s.length > 1 && s[1] === s[1].toLowerCase() ? s[0].toLowerCase() + s.slice(1) : s;
};

function championshipPara(facts: PaperFacts, turn: number): string {
  const c = facts.championship;
  const leader = c.top5[0];
  const second = c.top5[1];

  const head =
    leader && leader.moved !== null && leader.moved > 0
      ? // ⚠ NO "for the first time in a while". That was in this sentence for an
        // hour and it is an INVENTION: the facts say he moved up this week, and
        // say nothing whatever about how long it had been. The fallback writer
        // is held to D-003 exactly as the model is.
        `${c.leaderName} leaves ${trackInProse(facts.race?.track ?? '')} with the points lead, on ${plural(
          c.leaderPoints,
          'point',
        )}, up ${plural(leader.moved, 'place')} on the week.`
      : turn % 2 === 0
        ? `${c.leaderName} still leads the championship on ${plural(c.leaderPoints, 'point')}.`
        : `At the top of the standings nothing changed: ${c.leaderName}, ${plural(c.leaderPoints, 'point')}.`;

  const margin = second
    ? ` ${second.name} is ${plural(second.behind, 'point')} back${
        second.wins === 0 ? ' and has not won a race all year' : ''
      }.`
    : '';

  const rest =
    c.top5.length > 2
      ? ` Behind them: ${list(c.top5.slice(2).map((d) => `${d.name} ${ordinal(d.rank)}`))}.`
      : '';

  const movers =
    c.notableMoves.length > 0
      ? ` ${list(
          c.notableMoves.map(
            (m) => `${m.name} ${m.moved > 0 ? 'moved up' : 'dropped'} ${Math.abs(m.moved)} to ${ordinal(m.rank)}`,
          ),
        )}.`
      : '';

  const left =
    facts.racesRemaining > 0
      ? ` ${plural(facts.racesRemaining, 'race')} remain${facts.championship.inPlayoff ? ' in the playoff' : ''}.`
      : '';

  return `${head}${margin}${rest}${movers}${left}`;
}

function attritionPara(facts: PaperFacts): string {
  const race = facts.race;
  if (!race) return '';
  const bits: string[] = [];

  if (race.biggestMover) {
    const m = race.biggestMover;
    bits.push(
      `${m.name} came from ${ordinal(m.started)} to ${ordinal(m.finished)}, the biggest gain of the afternoon`,
    );
  }
  if (race.fellOut.length > 0) {
    /*
      GROUPED BY REASON, and it is not a nicety. Ungrouped, four broken cars
      produced "Skeet Turnage with a broken car, Boone Wingo with a broken car,
      Chip Deaton with a broken car and Jesse Pennington in a wreck" — the exact
      drone that tells a reader nobody wrote this.
    */
    const byReason = new Map<string, string[]>();
    for (const d of race.fellOut) {
      byReason.set(d.reason, [...(byReason.get(d.reason) ?? []), d.name]);
    }
    const clauses = [...byReason.entries()].map(
      ([reason, names]) => `${list(names)} ${outWord(reason, names.length)}`,
    );
    bits.push(`${list(clauses)} did not see the finish`);
  }
  if (race.failedToQualify.length > 0) {
    bits.push(`${list(race.failedToQualify)} failed to qualify`);
  }
  if (race.absent.length > 0) {
    bits.push(`${list(race.absent.map((a) => `${a.name} was not at the track — ${a.reason}`))}`);
  }
  if (bits.length === 0) return '';
  // Sentence per fact rather than one long chain: a paragraph of clauses joined
  // by semicolons is how generated prose announces itself.
  return `${bits.map((b) => `${b[0].toUpperCase()}${b.slice(1)}.`).join(' ')}`;
}

function closePara(facts: PaperFacts, turn: number): string {
  const milestone = facts.career.milestones[0];
  if (milestone) return milestoneSentence(milestone, facts);

  const season = facts.seasonSoFar;
  if (season.mostWins && season.mostWins.wins > 1) {
    const money = facts.career.moneyLeader;
    return (
      `${season.mostWins.name} has ${plural(season.mostWins.wins, 'win')} in ${plural(
        facts.racesRun,
        'race',
      )} this season, and ${plural(season.differentWinners, 'different driver')} ${
        season.differentWinners === 1 ? 'has' : 'have'
      } won one.` +
      (money ? ` ${money.name} has won ${dollars(money.winningsMb)} across ${plural(facts.career.seasonsPlayed, 'season')}.` : '')
    );
  }

  return turn % 2 === 0
    ? `${plural(season.differentWinners, 'different driver')} ${
        season.differentWinners === 1 ? 'has' : 'have'
      } won a race this season, with ${plural(facts.racesRemaining, 'race')} still to run.`
    : `The tour has ${plural(facts.racesRemaining, 'race')} left and ${plural(
        season.differentWinners,
        'different winner',
      )} so far.`;
}

function milestoneSentence(
  milestone: { name: string; kind: MilestoneKind; value: number },
  facts: PaperFacts,
): string {
  const seasons = facts.career.seasonsPlayed;
  switch (milestone.kind) {
    case 'first-career-win':
      return `It is the first win of ${milestone.name}'s career, in ${plural(milestone.value, 'start')} across ${plural(
        seasons,
        'season',
      )}.`;
    case 'career-win-milestone':
      return `The win is the ${ordinal(milestone.value)} of ${milestone.name}'s career.`;
    case 'first-win-of-season':
      return `It is ${milestone.name}'s first win of the year, and it comes in race ${milestone.value}.`;
    case 'career-start-milestone':
      return `${milestone.name} made his ${ordinal(milestone.value)} career start.`;
    case 'ended-a-drought':
      return `${milestone.name} has now gone ${plural(milestone.value, 'race')} since his last win.`;
    default:
      return `${milestone.name} is worth watching from here.`;
  }
}

// ---------------------------------------------------------------------------
// The season edition
// ---------------------------------------------------------------------------

function seasonLead(facts: PaperFacts): string {
  const c = facts.championship;
  const champ = c.championName ?? c.leaderName;
  const settled =
    c.championName && c.championName !== c.leaderName
      ? ` ${c.leaderName} led the standings on points, but the championship is decided in the last race and ${champ} won it.`
      : '';
  return `${champ} is the champion of the ${facts.league} tour, closing the season on ${plural(
    c.leaderPoints,
    'point',
  )} from ${plural(c.leaderWins, 'win')} in ${plural(facts.racesRun, 'race')}.${settled}`;
}

function seasonBody(facts: PaperFacts): string {
  const s = facts.seasonSoFar;
  const bits: string[] = [];
  bits.push(`${plural(s.differentWinners, 'different driver')} won a race this year`);
  if (s.mostWins) bits.push(`${s.mostWins.name} won the most, ${plural(s.mostWins.wins, 'of them', 'of them')}`);
  if (s.mostTurnsLed) bits.push(`${s.mostTurnsLed.name} led the most, ${plural(s.mostTurnsLed.turns, 'turn')}`);
  if (s.bestAverageFinish)
    bits.push(`${s.bestAverageFinish.name} had the best average finish at ${s.bestAverageFinish.average}`);
  if (s.biggestEarner) bits.push(`${s.biggestEarner.name} banked the most, ${dollars(s.biggestEarner.winningsMb)}`);
  const winless =
    s.winlessInTopFive.length > 0
      ? ` ${list(s.winlessInTopFive)} finished in the top five of the championship without winning a race.`
      : '';
  return `${bits.map((b) => `${b[0].toUpperCase()}${b.slice(1)}.`).join(' ')}${winless}`;
}

function seasonClose(facts: PaperFacts): string {
  const career = facts.career;
  const parts: string[] = [];
  if (career.winsLeader) {
    parts.push(
      `${career.winsLeader.name} now has ${plural(career.winsLeader.wins, 'career win')} across ${plural(
        career.winsLeader.seasons,
        'season',
      )}.`,
    );
  }
  if (career.moneyLeader) {
    parts.push(`${career.moneyLeader.name} leads the money list on ${dollars(career.moneyLeader.winningsMb)}.`);
  }
  if (career.championships.length > 1) {
    parts.push(
      `The title has been shared: ${list(
        career.championships.map((c) => `${c.name} ${plural(c.titles, 'time')}`),
      )}.`,
    );
  }
  parts.push('The winter re-rates every card before anybody races again.');
  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// D-EZ-102 — the league's winter news, and the winter edition
// ---------------------------------------------------------------------------

function recordSentence(r: NonNullable<PaperFacts['winter']>['recordsBroken'][number]): string {
  const v = (x: number) => (r.money ? dollars(x) : String(x));
  if (r.scope === 'season') {
    return `${r.name} set a new mark for ${r.title.replace(/^Most /, 'most ').replace(/ in a season$/, ' in a season')} at ${v(r.value)}${
      r.previousName !== null && r.previousValue !== null ? `, beating ${r.previousName}'s ${v(r.previousValue)}` : ''
    }.`;
  }
  return `${r.name} moved past ${r.previousName ?? 'the old mark'} for ${r.title.replace(/^Most /, 'most ')}, now at ${v(r.value)}.`;
}

/** One paragraph on the season edition: awards, records, retirements, the Hall. */
function leagueNewsPara(facts: PaperFacts): string {
  const w = facts.winter;
  if (!w) return '';
  const bits: string[] = [];
  const named = w.awards.filter((a) => a.title !== 'Champion').slice(0, 3);
  if (named.length > 0) bits.push(`The year's honours: ${list(named.map((a) => `${a.title}, ${a.name}`))}.`);
  if (w.recordsBroken.length > 0) bits.push(recordSentence(w.recordsBroken[0]));
  if (w.retirements.length > 0) {
    bits.push(`${list(w.retirements.slice(0, 3).map((r) => r.name))} ${w.retirements.length === 1 ? 'has' : 'have'} raced for the last time.`);
  }
  if (w.hallOfFame.length > 0) bits.push(`${list(w.hallOfFame.map((h) => h.name))} will go into the Hall of Fame.`);
  return bits.join(' ');
}

function winterHeadline(facts: PaperFacts): string {
  const w = facts.winter;
  if (w?.hallOfFame.length) return `${surname(w.hallOfFame[0].name)} headed for the Hall of Fame`;
  if (w?.retirements.length) {
    const r = w.retirements[0];
    return r.wins > 0 ? `${surname(r.name)} hangs up the helmet after ${plural(r.wins, 'win')}` : `${surname(r.name)} calls it a career`;
  }
  if (w?.recordsBroken.length) return `${surname(w.recordsBroken[0].name)} rewrites the record book`;
  return `Winter news from the ${facts.league} tour`;
}

function winterStandfirst(facts: PaperFacts): string {
  const w = facts.winter;
  const champ = w?.champion?.name ?? facts.championship.championName ?? facts.championship.leaderName;
  const parts = [`${champ} is the champion of season ${facts.season}`];
  if (w && w.retirements.length > 0) parts.push(`${plural(w.retirements.length, 'driver')} retiring`);
  if (w && w.hallOfFame.length > 0) parts.push(`${plural(w.hallOfFame.length, 'new Hall of Famer', 'new Hall of Famers')}`);
  return `${parts.join(' · ')}.`;
}

function winterParagraphs(facts: PaperFacts): string[] {
  const w = facts.winter;
  if (!w) return [seasonLead(facts)];
  const out: string[] = [];
  const champ = w.champion;
  if (champ) {
    out.push(
      `The off-season opens with ${champ.name} as champion, ${champ.titles > 1 ? `his ${wordOrdinal(champ.titles)} title` : 'his first title'}, won on ${plural(
        champ.points,
        'point',
      )} and ${plural(champ.wins, 'win')}.`,
    );
  }
  const honours = w.awards.filter((a) => a.title !== 'Champion');
  if (honours.length > 0) {
    const lead = honours.slice(0, 4).map((a) => `${a.name} is the ${a.title}: ${a.basis}.`);
    const rest = honours.slice(4).map((a) => `${a.title}, ${a.name}`);
    out.push(`${lead.join(' ')}${rest.length ? ` The rest of the year's honours: ${list(rest)}.` : ''}`);
  }
  if (w.recordsBroken.length > 0) out.push(w.recordsBroken.map(recordSentence).join(' '));
  if (w.retirements.length > 0) {
    out.push(
      w.retirements
        .slice(0, 6)
        .map(
          (r) =>
            `${r.name}${r.age !== null ? `, ${r.age},` : ''} retires after ${plural(r.seasons, 'season')}, ${plural(r.starts, 'start')} and ${plural(r.wins, 'win')}${
              r.championships > 0 ? ` and ${plural(r.championships, 'championship')}` : ''
            }.`,
        )
        .join(' '),
    );
  }
  if (w.lost.length > 0) {
    out.push(`The sport also remembers ${list(w.lost.map((l) => `${l.name}, lost in race ${l.raceNumber}`))}.`);
  }
  if (w.hallOfFame.length > 0) {
    out.push(
      w.hallOfFame
        .map((h) => `${h.name} goes into the Hall of Fame on ${h.citation}.`)
        .join(' '),
    );
  }
  if (w.sponsorNews.length > 0) {
    out.push(
      w.sponsorNews
        .map((s) =>
          s.kind === 'new-primary'
            ? `${s.name} has a new name on the hood for next season: ${s.sponsor}.`
            : `${s.sponsor} stays on ${s.name}'s car for a ${wordOrdinal(s.seasons)} season.`,
        )
        .join(' '),
    );
  }
  out.push('The winter re-rates every card before anybody races again.');
  return out;
}

/**
 * "2014 SONOMA RACEWAY" → "Sonoma Raceway".
 *
 * ⚠ THE ERA PREFIX AND THE SHOUTING BOTH HAVE TO GO. Every name on a published
 * track card is stamped with the era it is from and set in capitals, and both
 * survived into the body of the first article this writer produced: *"The Toyota
 * Save Mart 350 at 2014 SONOMA RACEWAY"*. A racing weekly does not print a year
 * inside a track's name and it does not shout.
 */
export function trackInProse(track: string): string {
  const bare = track.replace(/^\s*(19|20)\d{2}s?\s+/, '').trim();
  if (bare.length === 0) return track;
  // Only re-case names that arrived shouted; a properly-cased one is left alone.
  if (bare !== bare.toUpperCase()) return bare;
  return bare
    .toLowerCase()
    .split(' ')
    .map((w, i) => (w.length === 0 || (i > 0 && SMALL_WORDS.has(w)) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

/** The name a headline uses. Newspapers put surnames in headlines. */
export function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1] : name;
}

/** "2000s BRISTOL MOTOR SPEEDWAY" → "Bristol". Headlines have no room for the rest. */
export function shortTrack(track: string): string {
  const bare = track
    .replace(/^\s*(19|20)\d{2}s?\s+/, '')
    .replace(/\b(MOTOR\s+)?(SPEEDWAY|RACEWAY|INTERNATIONAL|SUPERSPEEDWAY|MOTORSPORTS?|PARK|COMPLEX)\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (bare.length === 0) return track;
  // Title case: these arrive shouted off the track cards and a headline is not.
  // Small joining words stay small ("Gateway at St. Louis", not "At").
  return bare
    .toLowerCase()
    .split(' ')
    .map((w, i) => (i > 0 && SMALL_WORDS.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');
}

const SMALL_WORDS = new Set(['at', 'of', 'the', 'and', 'on', 'in']);
